import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { useAuth } from "./auth.provider";
import { Notification } from "../types/notification";
import {
    getMyNotifications,
    getUnreadCount,
    markAsRead as markAsReadApi,
    markAllAsRead as markAllAsReadApi,
    sendFcmToken
} from "../libs/notifications";
import {
    initializeFirebase,
    requestNotificationPermission,
    onForegroundMessage
} from "../libs/firebase";
import { toast } from "react-hot-toast";

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    fetchNotifications: (limit?: number) => Promise<void>;
    markAsRead: (id: number) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Set to true once backend notification endpoints are ready
const NOTIFICATIONS_ENABLED = true;

const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, token } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [fcmInitialized, setFcmInitialized] = useState(false);
    const fcmInitializedRef = useRef(false);

    const initializeFCM = useCallback(async () => {
        if (!NOTIFICATIONS_ENABLED) return;

        try {
            initializeFirebase();
            const fcmToken = await requestNotificationPermission();

            if (fcmToken) {
                await sendFcmToken(fcmToken);
                console.log('FCM token sent to server');
            }

            // Listen for foreground messages
            onForegroundMessage((payload) => {
                console.log('Foreground message received:', payload);

                // Show toast notification
                toast(payload.notification?.title || 'New notification', {
                    icon: '🔔',
                    duration: 5000,
                });

                // Refresh notifications list and count
                fetchNotifications(10);
                refreshUnreadCount();
            });

            setFcmInitialized(true);
        } catch (error) {
            console.error('FCM initialization error:', error);
        }
    }, []);

    const fetchNotifications = useCallback(async (limit = 10) => {
        if (!NOTIFICATIONS_ENABLED || !user) return;

        setLoading(true);
        try {
            const response = await getMyNotifications({ limit });
            if (response.success) {
                setNotifications(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    const refreshUnreadCount = useCallback(async () => {
        if (!NOTIFICATIONS_ENABLED || !user) return;

        try {
            const response = await getUnreadCount();
            if (response.success) {
                setUnreadCount(response.data.count);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    }, [user]);

    const markAsRead = async (id: number) => {
        if (!NOTIFICATIONS_ENABLED) return;

        try {
            await markAsReadApi(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const markAllAsRead = async () => {
        if (!NOTIFICATIONS_ENABLED) return;

        try {
            await markAllAsReadApi();
            setNotifications(prev => prev.map(n => ({ ...n, status: 'read' as const, read_at: new Date().toISOString() })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // // Initialize Firebase and request permission when user logs in
    useEffect(() => {
        if (user && token && !fcmInitialized) {
            initializeFCM();
        }
    }, [user, token, fcmInitialized, initializeFCM]);

    // Fetch initial data when user logs in
    useEffect(() => {
        if (user && token) {
            fetchNotifications(10);
            refreshUnreadCount();
        }
    }, [user, token, fetchNotifications, refreshUnreadCount]);


    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            loading,
            fetchNotifications,
            markAsRead,
            markAllAsRead,
            refreshUnreadCount,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotifications must be used within a NotificationProvider");
    }
    return context;
};

export default NotificationProvider;
